import { useEffect, useState } from 'react';
import { NATIVE_OR_SIM } from '../lib/proximity/native/simPhone';
import PriiLogo from './PriiLogo';
import Button from './ui/Button';
import Modal, { ModalBody, ModalFooter } from './ui/Modal';
import { IOS_BETA_URL } from '../config/appLinks';
import { isWelcomeDemo } from '../lib/welcomeDemo';

/**
 * On the web only: one small modal saying the app exists, with the beta link (Sam, 2026-10-04: "hey, we're using
 * the app now" - front and centre, one time, a tap away from gone: the backdrop, Not now and Escape all close it).
 * Dismissed once per device, in localStorage. It waits for about two minutes of the site actually on screen (Sam,
 * 2026-10-22: straight away it "feels like bombardment"); the time adds up across visits, stops while the tab is
 * hidden, and holds while the welcome flow is on screen so two things never ask at once.
 */
const SEEN_KEY = 'prii-get-app-prompt';
const VIEWED_KEY = 'prii-get-app-viewed-ms';
const SHOW_AFTER_MS = 2 * 60 * 1000;
const TICK_MS = 5000;

function viewedMs() {
  try {
    return Number(window.localStorage.getItem(VIEWED_KEY)) || 0;
  } catch {
    return 0;
  }
}

function dismissed() {
  try {
    return window.localStorage.getItem(SEEN_KEY) === 'done';
  } catch {
    return true; // no storage: never nag
  }
}

export default function GetAppPrompt() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (NATIVE_OR_SIM || dismissed()) return undefined;
    // An invite link is its own introduction: never cover the join.
    if (/[?&]invite=/.test(window.location.search) || /\/invite\//.test(window.location.pathname))
      return undefined;
    let t = null;
    let ms = viewedMs();
    const tick = () => {
      if (document.visibilityState !== 'visible' || isWelcomeDemo(window.location.search)) return;
      ms += TICK_MS;
      try {
        window.localStorage.setItem(VIEWED_KEY, String(ms));
      } catch {
        /* private mode: this visit only */
      }
      if (ms >= SHOW_AFTER_MS) {
        clearInterval(t);
        t = null;
        setOpen(true);
      }
    };
    t = setInterval(tick, TICK_MS);
    return () => {
      if (t != null) clearInterval(t);
    };
  }, []);
  if (!open) return null;
  const close = () => {
    try {
      window.localStorage.setItem(SEEN_KEY, 'done');
    } catch {
      /* private mode */
    }
    setOpen(false);
  };
  return (
    <Modal isOpen={open} onClose={close} size="sm" showClose={false}>
      <ModalBody>
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <PriiLogo size="md" />
          <p className="text-lg font-semibold text-gray-900 dark:text-dark-text">
            Prii is an app now
          </p>
          <p className="text-sm text-gray-500 dark:text-dark-muted">
            Find your friends at festivals. iPhone beta.
          </p>
        </div>
      </ModalBody>
      <ModalFooter align="stack">
        <Button as="a" href={IOS_BETA_URL} target="_blank" rel="noopener" fullWidth onClick={close}>
          Get the app
        </Button>
        <Button type="button" variant="ghost" fullWidth onClick={close}>
          Not now
        </Button>
      </ModalFooter>
    </Modal>
  );
}
